/**
 * service-customer client. Mirrors:
 *   apps/service-customer/src/modules/customer/customer.controller.ts
 */
import { env } from '../env';

import { request } from './client';
import type {
  CreateCustomerRequest,
  CustomerLookupQuery,
  CustomerLookupResponse,
  CustomerResponse,
} from './types';

const baseUrl = (): string => env.serviceCustomerBaseUrl;

export async function lookupCustomers(
  query: CustomerLookupQuery,
): Promise<CustomerLookupResponse> {
  return request<CustomerLookupResponse>(baseUrl(), '/v1/customers/lookup', {
    method: 'GET',
    query: {
      phone: query.phone,
      email: query.email,
      fullName: query.fullName,
      customerNumber: query.customerNumber,
      page: query.page,
      pageSize: query.pageSize,
    },
  });
}

export async function createCustomer(dto: CreateCustomerRequest): Promise<CustomerResponse> {
  return request<CustomerResponse>(baseUrl(), '/v1/customers', {
    method: 'POST',
    body: dto,
  });
}

export async function getCustomerById(id: string): Promise<CustomerResponse> {
  return request<CustomerResponse>(baseUrl(), `/v1/customers/${encodeURIComponent(id)}`, {
    method: 'GET',
  });
}

// ---------------------------------------------------------------------------
// Citizen self-service surface (Customer/read-self) — used by the driver-portal
// KYC tab. NIN / BVN come back masked to last-4 digits.
// ---------------------------------------------------------------------------

export interface CitizenMeKycResponse {
  readonly customerId: string;
  readonly customerNumber: string;
  readonly fullName: string;
  /** NONE | BASIC | ENHANCED | INSTITUTION. */
  readonly kycLevel: string;
  readonly ninMasked: string | null;
  readonly bvnMasked: string | null;
  /** null until the service-kyc citizen-scoped surface lands. */
  readonly kycVerifiedAt: string | null;
  /** Always false until the citizen-scoped watchlist projection lands. */
  readonly sanctionsScreened: boolean;
  /** null — no doc store is citizen-exposed yet. */
  readonly documents: null;
}

export async function getMyKyc(): Promise<CitizenMeKycResponse> {
  return request<CitizenMeKycResponse>(baseUrl(), '/v1/customers/me/kyc', {
    method: 'GET',
  });
}

// ---------------------------------------------------------------------------
// Customer admin surface (Admin/listCustomers) — powers the staff console
// `/admin/customers` browse page. Mirrors:
//   apps/service-customer/src/modules/customer/customer.controller.ts
//     GET /v1/customers/admin
// and the DTOs at customer.dto.ts (AdminCustomerSummaryDto +
// ListAdminCustomersResponseDto).
// ---------------------------------------------------------------------------

export interface AdminCustomerPrimaryAddress {
  readonly state: string | null;
  readonly geoStateId: number | null;
  readonly city: string | null;
}

export interface AdminCustomerSummary {
  readonly id: string;
  readonly customerNumber: string;
  readonly fullName: string;
  readonly phone: string | null;
  readonly email: string | null;
  /** WALK_IN | REGISTERED. */
  readonly grade: string;
  readonly capturedByOperatorId: number;
  readonly createdAt: string;
  readonly primaryAddress: AdminCustomerPrimaryAddress | null;
}

export interface ListAdminCustomersResponse {
  readonly items: readonly AdminCustomerSummary[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly filters: Record<string, unknown>;
}

export interface ListAdminCustomersQuery {
  readonly q?: string;
  readonly grade?: string;
  readonly geoStateId?: number;
  readonly createdFrom?: string;
  readonly createdTo?: string;
  readonly outletId?: number;
  readonly page?: number;
  readonly pageSize?: number;
  /** `createdAt:desc | createdAt:asc | fullName:asc | fullName:desc`. */
  readonly sort?: string;
}

export async function listAdminCustomers(
  query: ListAdminCustomersQuery = {},
): Promise<ListAdminCustomersResponse> {
  return request<ListAdminCustomersResponse>(baseUrl(), '/v1/customers/admin', {
    method: 'GET',
    query: {
      q: query.q,
      grade: query.grade,
      geoStateId: query.geoStateId,
      createdFrom: query.createdFrom,
      createdTo: query.createdTo,
      outletId: query.outletId,
      page: query.page,
      pageSize: query.pageSize,
      sort: query.sort,
    },
  });
}

// ---------------------------------------------------------------------------
// Per-customer addresses — GET /v1/customers/:id/addresses. Used by the
// customer-360 detail page. Returns addresses primary-first.
// ---------------------------------------------------------------------------

export interface CustomerAddress {
  readonly id: string;
  readonly customerId: string;
  readonly label: string;
  readonly recipientName: string | null;
  readonly line1: string;
  readonly line2: string | null;
  readonly landmark: string | null;
  readonly city: string;
  readonly state: string;
  readonly postalCode: string | null;
  readonly countryCode: string;
  readonly isPrimary: boolean;
  readonly contactPhone: string | null;
  /** service-geo soft-FK ids + names. Populated when the address was captured
   *  via the cascading AddressFields picker. Nullable on legacy / non-NG rows. */
  readonly geoCountryId: number | null;
  readonly geoCountryName: string | null;
  readonly geoStateId: number | null;
  readonly geoStateName: string | null;
  readonly geoLgaId: number | null;
  readonly geoLgaName: string | null;
  readonly geoWardId: number | null;
  readonly geoWardName: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export async function listCustomerAddresses(
  customerId: string,
): Promise<readonly CustomerAddress[]> {
  return request<readonly CustomerAddress[]>(
    baseUrl(),
    `/v1/customers/${encodeURIComponent(customerId)}/addresses`,
    { method: 'GET' },
  );
}
